import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, LogIn, Users, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';

function LandingPage() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/images/logo.png" 
                alt="Set in Stone Didcot" 
                className="h-16 w-auto"
              />
              <div>
                <h1 className="text-2xl font-bold text-white">Set in Stone Didcot</h1>
                <p className="text-blue-200 text-sm mt-1">Premium Granite & Quartz Worktops</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-white text-blue-900 hover:bg-blue-50"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Staff Login
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Transform Your Kitchen
          </h2>
          <p className="text-xl text-blue-200 mb-12 max-w-3xl mx-auto">
            Quality worktops in Granite and Quartz. Professional installation, expert craftsmanship, 
            competitive prices. Get your free quote today.
          </p>

          {/* Instant Quote Highlight Section */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl shadow-2xl p-8 mb-12 max-w-5xl mx-auto transform hover:scale-105 transition-all duration-300">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-left flex-1">
                <div className="inline-block bg-white/20 text-white px-3 py-1 rounded-full text-sm font-semibold mb-3">
                  ⚡ NEW FEATURE
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Get Your Quote Instantly Online
                </h3>
                <p className="text-lg text-green-50 mb-4">
                  Choose from our premium stone materials and see your price in real-time. 
                  No waiting, no hassle - get an instant estimate for your dream worktop project.
                </p>
                <ul className="space-y-2 text-green-50">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span>Select from Budget Friendly, Mid Range, or Premium materials</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span>See real-time pricing as you design</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span>Browse our curated granite & quartz collection</span>
                  </li>
                </ul>
              </div>
              <div className="flex-shrink-0">
                <Button 
                  onClick={() => navigate('/quote')}
                  className="bg-white text-green-600 hover:bg-green-50 text-xl py-8 px-12 rounded-xl shadow-xl font-bold"
                  size="lg"
                >
                  Get Instant Quote →
                </Button>
              </div>
            </div>
          </div>

          {/* Main CTA Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
            {/* Get Quote Card */}
            <Card className="bg-white/95 backdrop-blur hover:bg-white transition-all duration-300 transform hover:scale-105 shadow-2xl">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl text-gray-900">Need a Custom Quote?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Looking for a specific stone colour or custom design? Get a personalized quote within 24 hours.
                </p>
                <Button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                  size="lg"
                >
                  Request 24h Quote
                </Button>
              </CardContent>
            </Card>

            {/* Hearth Quote Card */}
            <Card className="bg-white/95 backdrop-blur hover:bg-white transition-all duration-300 transform hover:scale-105 shadow-2xl">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                  </svg>
                </div>
                <CardTitle className="text-2xl text-gray-900">Fireplace Hearths</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Custom granite hearths in various shapes. Choose from T-shape, rectangle, quadrant, or custom designs.
                </p>
                <Button 
                  onClick={() => navigate('/hearth-quote')}
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white py-6 text-lg"
                  size="lg"
                >
                  Get Hearth Quote
                </Button>
              </CardContent>
            </Card>

            {/* Trade Account Card */}
            <Card className="bg-white/95 backdrop-blur hover:bg-white transition-all duration-300 transform hover:scale-105 shadow-2xl">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
                <CardTitle className="text-2xl text-gray-900">Trade Account</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Join our trade program for exclusive discounts, priority service, and dedicated support.
                </p>
                <Button 
                  onClick={() => navigate('/apply-trade')}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg"
                  size="lg"
                >
                  Apply for Trade Account
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 px-4 bg-white/5 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl font-bold text-white text-center mb-12">
            Why Choose Set in Stone Didcot?
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2">Premium Materials</h4>
                <p className="text-blue-200">
                  High-quality Granite and Quartz from trusted suppliers
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2">Expert Installation</h4>
                <p className="text-blue-200">
                  Professional fitting with precision templating
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2">Competitive Pricing</h4>
                <p className="text-blue-200">
                  Great value with transparent quotes and no hidden fees
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white/95 backdrop-blur shadow-2xl">
            <CardHeader>
              <CardTitle className="text-3xl text-center text-gray-900">Get In Touch</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="font-semibold text-gray-900">Phone</p>
                  <a href="tel:01235812500" className="text-blue-600 hover:text-blue-700 font-medium">
                    01235 812500
                  </a>
                  <p className="text-gray-600 text-xs mt-1">Call us for immediate assistance</p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="font-semibold text-gray-900">Email</p>
                  <a href="mailto:worktops@setinstonedidcot.co.uk" className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                    worktops@setinstonedidcot.co.uk
                  </a>
                </div>
                <div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="font-semibold text-gray-900">Location</p>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=OX11+8AG" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm block"
                  >
                    Didcot, OX11 8AG
                  </a>
                  <p className="text-gray-600 text-xs mt-1">Serving Didcot and surrounding areas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white/5 backdrop-blur-sm border-t border-white/10 py-8 px-4 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-blue-200">
            © {currentYear} Set in Stone Didcot Ltd. All rights reserved.
          </p>
          <div className="mt-4 space-x-4">
            <button 
              onClick={() => navigate('/login')}
              className="text-blue-300 hover:text-white transition-colors"
            >
              Staff Login
            </button>
            <span className="text-blue-400">•</span>
            <button 
              onClick={() => navigate('/apply-trade')}
              className="text-blue-300 hover:text-white transition-colors"
            >
              Trade Account
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
// force rebuild
